import { useIsAuthenticated } from '@azure/msal-react'
import AccountCircleIcon from '@mui/icons-material/AccountCircle'
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft'
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight'
import MenuIcon from '@mui/icons-material/Menu'
import { AppBar, Box, IconButton, Toolbar } from '@mui/material'
import { Inbox } from '@novu/react'
import DOMPurify from 'dompurify'
import parse from 'html-react-parser'
import { SyntheticEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'

import { novuAppIdentifier } from '../../authConfig'
import { PopoverMenu } from '../../components/PopoverMenu'
import PMPLogo from '../../logo.png'
import { useAppSelector, useCurrentSite } from '../../store/store.hooks'
import { useUserFullName, useUserSitesByOrg } from '../../user/hooks/user.hooks'
import AdminBreadcrumbBar from './AdminBreadcrumbBar'
import BreadcrumbBar from './BreadcrumbBar'
import HelpMenu from './HelpMenu'

type Props = {
  onMenuClick: () => void
  onSiteClick: () => void
  isSmallDown: boolean
  showHamburger: boolean
  desktopOpen: boolean
  menuItems: { title: string; action: () => void }[]
}

const AppTopBar = ({ onMenuClick, onSiteClick, isSmallDown, showHamburger, desktopOpen, menuItems }: Props) => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const isAuthenticated = useIsAuthenticated()

  const name = useUserFullName() ?? useAppSelector((state) => state.auth.name)
  const novuSubscriberInfo = useAppSelector((state) => state.auth.novuSubscriberInfo)

  const location = useLocation()

  const TOPBAR_ICON_SIZE = '1.25rem'

  const sites = useUserSitesByOrg()
  const allSites = sites.flatMap((org) => org.sites)
  const currentSite = useCurrentSite()
  const logoSrc = currentSite?.logoUri || PMPLogo

  return (
    <AppBar position="fixed" color="secondary" sx={(theme) => ({ zIndex: theme.zIndex.drawer + 1 })}>
      <Toolbar sx={{ justifyContent: 'space-between', m: 0, p: 0, pl: '16px !important', pr: '16px !important' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'nowrap' }}>
          <IconButton
          id="hamburger-button"
            sx={{ display: showHamburger ? 'flex' : 'none', mr: 1, ml: 0, padding: 0 }}
            edge="start"
            color="inherit"
            aria-label="menu"
            onClick={onMenuClick}
          >
            {isSmallDown ? <MenuIcon /> : desktopOpen ? <KeyboardDoubleArrowLeftIcon /> : <KeyboardDoubleArrowRightIcon />}
          </IconButton>
          <Box
          id="logo-container"
            component="img"
            src={logoSrc}
            alt="PMP Logo"
            sx={{ maxHeight: currentSite?.logoUri ? 40 : 60, cursor: 'pointer', display: { xs: 'none', sm: 'block' } }}
            onClick={() => (allSites.length > 0 ? navigate('/hub') : navigate('/'))}
            onError={(e: SyntheticEvent<HTMLImageElement>) => {
              e.currentTarget.src = PMPLogo
              e.currentTarget.style.maxHeight = '60px'
            }}
          />
          {location.pathname.startsWith('/admin') || location.pathname.startsWith('/adminReports') ? (
            <AdminBreadcrumbBar />
          ) : (
            <BreadcrumbBar onSiteClick={onSiteClick} />
          )}
        </Box>

        <Box id="topbar-actions-right" sx={{ display: 'flex', alignItems: 'center', flexWrap: 'nowrap' }}>
          {novuSubscriberInfo?.subscriberId && novuSubscriberInfo?.subscriberHash && (
            <Inbox
              applicationIdentifier={`${novuAppIdentifier}`}
              subscriberId={novuSubscriberInfo.subscriberId}
              subscriberHash={novuSubscriberInfo.subscriberHash}
              appearance={{ elements: { bellIcon: { color: 'white', fontSize: TOPBAR_ICON_SIZE } } }}
              renderBody={(notification) => {
                const cleanHtml = DOMPurify.sanitize(notification.body || '')
                return <div>{parse(cleanHtml)}</div>
              }}
            />
          )}

          <HelpMenu />
          {isAuthenticated && (
            <>
              <Box sx={{ mr: 2, ml: 2, display: isSmallDown ? 'none' : 'block', whiteSpace: 'nowrap' }}>{name}</Box>
              <Box sx={{ display: isSmallDown ? 'block' : 'none', mr: 1 }}>
                <AccountCircleIcon sx={{ color: 'white', fontSize: TOPBAR_ICON_SIZE, mt: 0.5 }} />
              </Box>
              <PopoverMenu menuItems={menuItems} />
            </>
          )}
          {!isAuthenticated && t('notConnected')}
        </Box>
      </Toolbar>
    </AppBar>
  )
}

export default AppTopBar
